import { ApiService } from "./apiService";
import { withIsActive, failure } from "./serviceHelpers";
import { AuthResponse, BiaFitUser } from "../types/user";

export interface AuthResult {
  success: boolean;
  user?: BiaFitUser;
  token?: string;
  error?: string;
}

export interface AuthUser {
  id: number;
  email: string;
  isAuthenticated: boolean;
}

export class AuthService {
  // Sign in with email and password
  static async signIn(
    email: string,
    password: string,
    rememberMe: boolean = false
  ): Promise<AuthResult> {
    try {
      const response = await ApiService.post("/auth/login", {
        email,
        password,
      });

      const authResponse: AuthResponse = await ApiService.handleResponse(
        response
      );

      // Store token for future requests
      ApiService.setAuthToken(authResponse.data.token);

      // Choose storage based on rememberMe preference
      const storage = rememberMe ? localStorage : sessionStorage;

      // Store tokens with session preference and timestamp
      storage.setItem("auth_token", authResponse.data.token);
      storage.setItem("refresh_token", authResponse.data.refresh_token);
      storage.setItem("remember_me", rememberMe.toString());
      storage.setItem("login_timestamp", Date.now().toString());

      // Clear opposite storage to prevent conflicts
      const oppositeStorage = rememberMe ? sessionStorage : localStorage;
      oppositeStorage.removeItem("auth_token");
      oppositeStorage.removeItem("refresh_token");
      oppositeStorage.removeItem("remember_me");
      oppositeStorage.removeItem("login_timestamp");

      // Convert auth response to BiaFitUser format
      const user: BiaFitUser = {
        id: authResponse.data.id,
        email: authResponse.data.email,
        first_name: authResponse.data.first_name,
        last_name: authResponse.data.last_name,
        gender: authResponse.data.gender,
        date_of_birth: authResponse.data.date_of_birth || null,
        height: authResponse.data.height || null,
        email_verified_at: authResponse.data.email_verified_at,
        created_at: authResponse.data.created_at,
        updated_at: authResponse.data.updated_at,
        deleted_at: authResponse.data.deleted_at,
        role: authResponse.data.role,
        is_active: !authResponse.data.deleted_at,
      };

      // Check if user is admin
      if (user.role !== "admin") {
        this.signOut(); // Clear any stored tokens
        return {
          success: false,
          error: "Access denied. Only authorized administrators can sign in.",
        };
      }

      return {
        success: true,
        user,
        token: authResponse.data.token,
      };
    } catch (err) {
      return failure(err, "An unexpected error occurred during sign in");
    }
  }

  // Sign out
  static async signOut(): Promise<AuthResult> {
    try {
      // Clear tokens from both storage locations
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("remember_me");
      localStorage.removeItem("login_timestamp");
      sessionStorage.removeItem("auth_token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("remember_me");
      sessionStorage.removeItem("login_timestamp");

      // Clear from API service
      ApiService.clearAuthToken();

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: "An unexpected error occurred during sign out",
      };
    }
  }

  // Get stored token from appropriate storage
  private static getStoredToken(): {
    token: string | null;
    rememberMe: boolean;
    loginTime: number | null;
  } {
    // Try localStorage first (persistent sessions)
    let token = localStorage.getItem("auth_token");
    let loginTime = localStorage.getItem("login_timestamp");

    if (token) {
      return {
        token,
        rememberMe: true,
        loginTime: loginTime ? parseInt(loginTime) : null,
      };
    }

    // Try sessionStorage (temporary sessions)
    token = sessionStorage.getItem("auth_token");
    loginTime = sessionStorage.getItem("login_timestamp");

    return {
      token,
      rememberMe: false,
      loginTime: loginTime ? parseInt(loginTime) : null,
    };
  }

  // Check if session has expired (now only checks refresh token validity period)
  private static isSessionExpired(
    loginTime: number | null,
    rememberMe: boolean
  ): boolean {
    if (!loginTime) return true;

    const now = Date.now();
    // Refresh token validity period - longer than access token
    const sessionDuration = rememberMe
      ? 30 * 24 * 60 * 60 * 1000 // 30 days for persistent
      : 24 * 60 * 60 * 1000; // 24 hours for session (refresh token lifetime)

    return now - loginTime > sessionDuration;
  }

  // Get current user from API
  static async getCurrentUser(): Promise<BiaFitUser | null> {
    try {
      const { token, rememberMe, loginTime } = this.getStoredToken();

      // Check if we have either access token or refresh token
      const refreshToken =
        localStorage.getItem("refresh_token") ||
        sessionStorage.getItem("refresh_token");
      if (!token && !refreshToken) {
        return null;
      }

      // Check if refresh token has expired (much longer than access token)
      if (this.isSessionExpired(loginTime, rememberMe)) {
        this.signOut();
        return null;
      }

      // Set token for API request - ApiService will handle refresh automatically
      if (token) {
        ApiService.setAuthToken(token);
      }

      const response = await ApiService.get("/me");
      const result = await ApiService.handleResponse<any>(response);

      // Handle both response formats: direct user object or nested in 'data'
      const userData = result.data || result;

      if (userData && userData.id) {
        return withIsActive(userData);
      }

      return null;
    } catch (err: any) {
      // ApiService already handles token refresh and clearing internally
      // Don't clear tokens here as it may interfere with the refresh process
      return null;
    }
  }

  // Check if user is authenticated
  static async isAuthenticated(): Promise<boolean> {
    try {
      const { token } = this.getStoredToken();
      if (!token) {
        return false;
      }

      const user = await this.getCurrentUser();
      return !!user && user.role === "admin" && user.is_active;
    } catch (err) {
      return false;
    }
  }

  // Initialize auth from stored token
  static async initializeAuth(): Promise<BiaFitUser | null> {
    try {
      const { token } = this.getStoredToken();
      const refreshToken =
        localStorage.getItem("refresh_token") ||
        sessionStorage.getItem("refresh_token");

      // Return null only if we have neither access token nor refresh token
      if (!token && !refreshToken) {
        return null;
      }

      // Set access token if available (ApiService will handle refresh automatically if needed)
      if (token) {
        ApiService.setAuthToken(token);
      }

      // Try to get current user - this will trigger automatic token refresh if needed
      const user = await this.getCurrentUser();
      return user;
    } catch (err) {
      // Don't clear tokens on initialization errors - could be network issues
      return null;
    }
  }

  // Password reset (placeholder - implement when backend supports it)
  static async resetPassword(_email: string): Promise<AuthResult> {
    // This would need to be implemented in your backend
    // For now, return a not implemented message
    return {
      success: false,
      error: "Password reset not implemented yet",
    };
  }
}
